import * as React from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { toast } from "sonner";
import { Receipt, Plus, Printer, Filter, TrendingDown } from "lucide-react";
import { EmptyState } from "@/components/feel/EmptyState";
import InvoiceDrawer from "./InvoiceDrawer";

const STATUS_STYLES = {
  draft:  "bg-brand-rule/60 text-brand-muted",
  issued: "bg-amber-100 text-amber-800",
  paid:   "bg-emerald-100 text-emerald-800",
  void:   "bg-coral/15 text-coral",
};

/**
 * InvoicesPanel — Books > Invoices tab.
 *
 * Two stacked views:
 *   1. Aged debtors — 4-bucket outstanding table (mono numerals).
 *   2. Full invoice list — filter by status, click through to drawer.
 *
 * "New invoice" and status transitions live on the drawer.
 */
export default function InvoicesPanel({ companyId, canManage }) {
  const [aged, setAged] = React.useState(null);
  const [invoices, setInvoices] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [drawer, setDrawer] = React.useState(null); // {mode:'edit'|'new', id?}
  const [customers, setCustomers] = React.useState([]);

  const load = React.useCallback(async () => {
    try {
      const [a, i, c] = await Promise.all([
        api.get(`/companies/${companyId}/invoices/aged-debtors`),
        api.get(`/companies/${companyId}/invoices`, {
          params: statusFilter ? { status: statusFilter } : {},
        }),
        api.get(`/companies/${companyId}/customers`),
      ]);
      setAged(a.data);
      setInvoices(i.data.invoices || []);
      setCustomers(c.data.customers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load invoices");
    }
  }, [companyId, statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  if (invoices === null || aged === null) {
    return <div className="p-4 text-[12px] text-brand-muted">Loading…</div>;
  }

  return (
    <div data-testid="invoices-panel">
      {/* Aged debtors */}
      <section className="mb-6" data-testid="aged-debtors">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <div className="micro-label">Aged debtors</div>
            <div className="text-[13px] text-brand-ink">Outstanding issued invoices bucketed by days overdue.</div>
          </div>
          <div className="text-right">
            <div className="micro-label">Total outstanding</div>
            <div className="mono-num text-[18px] font-bold text-brand-ink">
              {fmtCents(aged.total_outstanding_cents)}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {["0-30", "31-60", "61-90", "90+"].map((k) => (
            <div key={k} className="card-panel p-3" data-testid={`aged-bucket-${k}`}>
              <div className="micro-label">{k} days</div>
              <div className="mono-num text-[17px] font-bold text-brand-ink mt-1">
                {fmtCents(aged.totals_cents[k] || 0)}
              </div>
              <div className="mono-num text-[10.5px] text-brand-muted mt-0.5">
                {(aged.buckets[k] || []).length} invoice{(aged.buckets[k] || []).length === 1 ? "" : "s"}
              </div>
              {(aged.buckets[k] || []).slice(0, 3).map((r) => (
                <div key={r.id} className="mt-2 text-[11px] text-brand-muted flex items-baseline justify-between gap-2 cursor-pointer hover:text-brand-ink"
                     onClick={() => setDrawer({ mode: "edit", id: r.id })}
                     data-testid={`aged-row-${r.id}`}>
                  <span className="truncate">{r.number} · {r.customer_name}</span>
                  <span className="mono-num shrink-0">{fmtCents(r.total_cents)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Full invoice list */}
      <section data-testid="invoices-list">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-brand-muted" />
            <div className="flex items-center gap-1">
              {["", "draft", "issued", "paid", "void"].map((s) => (
                <button key={s || "all"} onClick={() => setStatusFilter(s)}
                        className={`px-2 py-1 text-[11.5px] rounded-md ${statusFilter === s ? "bg-brand-violet text-white" : "text-brand-muted hover:bg-brand-canvas-2"}`}
                        data-testid={`invoices-filter-${s || "all"}`}>
                  {s || "all"}
                </button>
              ))}
            </div>
          </div>
          {canManage && (
            <button className="btn-violet h-8 text-[12px]"
                    onClick={() => setDrawer({ mode: "new" })}
                    disabled={customers.length === 0}
                    data-testid="invoices-new">
              <Plus size={12}/> New invoice
            </button>
          )}
        </div>

        {customers.length === 0 && canManage && (
          <div className="mb-3 rounded-md border border-brand-rule bg-brand-canvas px-3 py-2 flex items-center gap-2 text-[12px] text-brand-muted">
            <TrendingDown size={13}/> Add a customer first (Customers tab).
          </div>
        )}

        {invoices.length === 0 ? (
          <EmptyState icon={Receipt} eyebrow="Invoices"
                      title="No invoices."
                      body={statusFilter ? `No invoices in status '${statusFilter}'.` : "Create an invoice from the button above."}/>
        ) : (
          <div className="card-panel overflow-hidden">
            <table className="fable-table" data-testid="invoices-table">
              <thead>
                <tr>
                  <th className="w-[120px]">Number</th>
                  <th>Customer</th>
                  <th className="w-[110px]">Issued</th>
                  <th className="w-[110px]">Due</th>
                  <th className="text-right w-[120px]">Total</th>
                  <th className="w-[90px]">Status</th>
                  <th className="w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="cursor-pointer"
                      onClick={() => setDrawer({ mode: "edit", id: inv.id })}
                      data-testid={`invoice-row-${inv.id}`}>
                    <td className="mono-num font-medium">{inv.number}</td>
                    <td>{inv.customer_snapshot?.name || "—"}</td>
                    <td className="mono-num text-[11.5px] text-brand-muted">{inv.issue_date}</td>
                    <td className="mono-num text-[11.5px] text-brand-muted">{inv.due_date}</td>
                    <td className="text-right mono-num">{fmtCents(inv.total_cents)}</td>
                    <td>
                      <span className={`chip mono-num text-[10px] ${STATUS_STYLES[inv.status] || ""}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()} className="text-right">
                      <a href={`/c/${companyId}/invoices/${inv.id}/print`} target="_blank" rel="noreferrer"
                         className="text-brand-muted hover:text-brand-violet inline-flex items-center gap-1 text-[11px]"
                         data-testid={`invoice-print-${inv.id}`}>
                        <Printer size={11}/> Print
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InvoiceDrawer
        open={!!drawer}
        mode={drawer?.mode}
        invoiceId={drawer?.id}
        companyId={companyId}
        canManage={canManage}
        customers={customers}
        onClose={() => setDrawer(null)}
        onSaved={() => { setDrawer(null); load(); }}
      />
    </div>
  );
}
